import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, ApiRequestError } from '@astro/api'
import { resetPasswordSchema } from '@astro/validators'
import { useToast } from '../context/ToastContext'
import AuthScene, { ReturnHomeLink } from '../components/auth/AuthScene'
import { InputField, PasswordField, MailIcon } from '../components/auth/AuthControls'
import { toFieldErrors, userFacingMessage } from '../components/auth/formErrors'

/**
 * Password reset — step 2 of 2, opened by the link in the reset email:
 *   /reset-password?token=<token>&email=<address>
 * The token is only valid for that address, so both travel in the link; the
 * email stays editable in case a mail client mangled the query string.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showToast } = useToast()

  const token = searchParams.get('token') ?? ''

  const [form, setForm] = useState({
    email: searchParams.get('email') ?? '',
    password: '',
    password_confirmation: '',
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    const parsed = resetPasswordSchema.safeParse({ ...form, token })
    if (!parsed.success) return setErrors(toFieldErrors(parsed.error.issues))

    setErrors({})
    setSubmitting(true)
    try {
      const res = await apiClient.post('/reset-password', parsed.data)
      setDone(true)
      showToast(res?.message || 'Password updated. Please sign in with your new password.')
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setErrors(toFieldErrors(err.error.details))
        showToast(userFacingMessage(err, 'Could not reset your password. Please request a new link.'))
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // A link that lost its token can't be recovered here — send them back to
  // request a fresh one rather than letting them fill in a doomed form.
  if (!token) {
    return (
      <AuthScene>
        <div className="auth-view-panel">
          <div className="space-y-2 mb-8 text-center sm:text-left">
            <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">Link Expired</h2>
            <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">Reset Token Missing</p>
          </div>
          <p className="text-sm text-stone-500 leading-relaxed mb-6">
            This password reset link is incomplete or has already been used. Request a new one and we'll email a fresh link.
          </p>
          <Link to="/login?mode=forgot" className="shine-btn block text-center w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5">
            Request New Link →
          </Link>
        </div>
        <ReturnHomeLink />
      </AuthScene>
    )
  }

  return (
    <AuthScene>
      <div className="auth-view-panel">
        <div className="space-y-2 mb-8 text-center sm:text-left">
          <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">
            {done ? 'Password Updated' : 'Set New Password'}
          </h2>
          <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">
            {done ? 'Access Key Rewritten' : 'Choose Your New Access Key'}
          </p>
        </div>

        {done ? (
          <div className="space-y-5">
            <div className="p-5 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/70 border border-amber-800/15 flex items-center justify-center text-goldAccent text-lg shrink-0">✓</div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-amber-900 leading-tight">Your password has been changed</p>
                <p className="text-sm text-stone-500 font-light leading-relaxed mt-1">
                  Sign in with your new password to continue. This reset link is now spent and cannot be reused.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5"
            >
              Sign In Now →
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <InputField
              label="Email Address"
              labelClass="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2"
              icon={<MailIcon />}
              type="email"
              id="reset-email"
              autoComplete="email"
              required
              placeholder="seeker@path.com"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              error={errors.email}
            />
            <PasswordField
              label="New Password"
              labelClass="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2"
              autoComplete="new-password"
              required
              placeholder="Minimum 8 complex characters"
              value={form.password}
              onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
              error={errors.password}
            />
            <PasswordField
              label="Confirm New Password"
              labelClass="block text-[11px] font-bold uppercase tracking-[0.18em] text-stone-600 mb-2"
              autoComplete="new-password"
              required
              placeholder="Re-enter your new password"
              value={form.password_confirmation}
              onChange={(e) => setForm((prev) => ({ ...prev, password_confirmation: e.target.value }))}
              error={errors.password_confirmation}
            />

            {/* The token rides in the URL, so surface its rejection somewhere. */}
            {errors.token && <p className="text-xs text-red-600 tracking-wide">{errors.token}</p>}

            <button type="submit" disabled={submitting} className="shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.22em] uppercase py-4 rounded-xl shadow-md shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? 'Updating Password…' : 'Update Password →'}
            </button>

            <p className="text-center text-sm text-stone-500 pt-1">
              Remembered it?{' '}
              <Link to="/login" className="text-goldAccent font-semibold hover:underline outline-none focus:underline">Back to Sign In</Link>
            </p>
          </form>
        )}
      </div>

      <ReturnHomeLink />
    </AuthScene>
  )
}
